import {useCallback} from 'react';
import {useSelector} from 'react-redux';
import {useNavigator} from '~/common/FabricInvitation/EventCreateDesign/components/NavBottom/hooks/useNavigator';
import {Sections} from '~/common/FabricInvitation/EventCreateDesign/constants';
import {useFormValidations} from '~/common/FabricInvitation/hooks/useFormValidations';
import {useUyo} from '~/common/fabric/Card/hooks/useUyo';
import {
  selectDesignHasLogo,
  selectFabricArt,
  selectOriginalFabricArt,
} from '~/common/fabric/reducers/fabricDesign/selectors';
import type {FabricArt} from '~/common/fabric/types';
import {selectQuickInvite} from '~/OneStepCreateInvitation/reducers/fabricTemplate/selector';

export const useEventValidation = () => {
  const navigate = useNavigator({});
  const {isUyo, pristineUyo, pristineLogo} = useUyo();
  const hasLogo = useSelector(selectDesignHasLogo);
  const quickInvite = useSelector(selectQuickInvite);
  const fabricArt: FabricArt = useSelector(selectFabricArt);
  const originalFabricArt: FabricArt = useSelector(selectOriginalFabricArt);

  // Form validations
  const validateText = useCallback(() => {
    if (quickInvite) return null;

    const fabricArtTextboxes = fabricArt.objects.filter((obj) => obj.type === 'textbox');
    const originalTexts = new Set(
      originalFabricArt.objects.filter(({type}) => type === 'textbox').map(({text}) => text)
    );

    if (originalTexts.size <= 0) return null;

    const unchangedText = fabricArtTextboxes.every(({text}) => originalTexts.has(text));
    if (!unchangedText) return null;

    navigate(Sections.CARD_DESIGN);
    return {
      getMessage: () => 'Edit sample text on invitation',
      top: 0,
      order: 1,
      ref: document.querySelector('[data-qa-id="edit-design"]') as HTMLElement | null,
    };
  }, [quickInvite, fabricArt, originalFabricArt, navigate]);

  const validateUYO = useCallback(() => {
    if (isUyo && pristineUyo) {
      navigate(Sections.CARD_DESIGN);
      return {
        getMessage: () => 'Upload image to invitation',
        top: 0,
        order: 0,
        ref: document.querySelector('[data-qa-id="edit-design"]') as HTMLElement | null,
      };
    }
    return null;
  }, [isUyo, pristineUyo, navigate]);

  const validateLogo = useCallback(() => {
    if (hasLogo && pristineLogo) {
      navigate(Sections.CARD_DESIGN);
      return {
        getMessage: () => 'Upload logo to invitation',
        top: 0,
        order: 0,
        ref: document.querySelector('[data-qa-id="edit-design"]') as HTMLElement | null,
      };
    }
    return null;
  }, [hasLogo, pristineLogo, navigate]);

  useFormValidations('designText', validateText);
  useFormValidations('designUYO', validateUYO);
  useFormValidations('designLogo', validateLogo);
};
